import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";

const MAX_POLLS = 20;

const Panel = ({ icon, title, text, children }) => (
  <div data-testid="payment-result" className="mx-auto max-w-md rounded-3xl border border-white/10 bg-nw-surface/70 p-8 text-center space-y-4 animate-rise">
    <div className="flex justify-center">{icon}</div>
    <h1 data-testid="payment-result-title" className="font-display text-3xl font-extrabold text-white">{title}</h1>
    <p className="text-sm text-slate-400">{text}</p>
    <div className="flex justify-center gap-3">{children}</div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh, request } = useProfile();
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState("");
  const subId = params.get("subscription_id");

  useEffect(() => {
    if (!subId) { setState("failed"); return; }
    let tries = 0, timer, alive = true;
    const poll = async () => {
      try {
        const data = await request("post", "/payments/paypal/confirm", { subscription_id: subId });
        if (!alive) return;
        setPlan(data.plan);
        if (data.status === "ACTIVE") { await refresh(); setState("paid"); return; }
        if (["CANCELLED", "EXPIRED", "SUSPENDED"].includes(data.status)) { setState("failed"); return; }
      } catch (e) {
        if (e?.response?.status === 404) { if (alive) setState("failed"); return; }
      }
      if (++tries >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [subId, refresh, request]);

  if (state === "checking") return <Panel icon={<Loader2 className="h-10 w-10 animate-spin text-nw-red" />} title="Confirming payment" text="Hang tight while we confirm your subscription with PayPal." />;
  if (state === "paid") return (
    <Panel icon={<CheckCircle2 className="h-10 w-10 text-nw-red" />} title={`Welcome to ${plan === "pro" ? "Pro" : "Creator"}`} text="Your plan is active. You can upload your AI music and videos now.">
      <Link data-testid="payment-go-upload" to="/upload" className="btn-red px-5 py-2.5 text-sm">Start uploading</Link>
      <Link data-testid="payment-go-profile" to="/profile" className="btn-chrome px-5 py-2.5 text-sm">View profile</Link>
    </Panel>
  );
  return (
    <Panel icon={<XCircle className="h-10 w-10 text-slate-400" />} title={state === "timeout" ? "Still processing" : "Payment not completed"}
      text={state === "timeout" ? "PayPal hasn't confirmed yet. Refresh this page in a minute." : "Your payment didn't go through. You haven't been charged."}>
      <Link data-testid="payment-try-again" to="/upload" className="btn-red px-5 py-2.5 text-sm">Back to plans</Link>
    </Panel>
  );
}

export function PaymentCancel() {
  return (
    <Panel icon={<XCircle className="h-10 w-10 text-slate-400" />} title="Checkout canceled" text="No payment was taken. You can pick a plan any time.">
      <Link data-testid="payment-try-again" to="/upload" className="btn-red px-5 py-2.5 text-sm">Back to plans</Link>
    </Panel>
  );
}
